import React, { useEffect, useState, useRef } from "react";
import AdminDashboard from "./admindashboard";
import axios from "../api/baseurl";

import { useNavigate, useParams } from "react-router-dom";
import "./Spinner.css";
import "./Loader.css";
import "./Stepper.css";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { Modal, Button, Pagination } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import { FiInfo } from "react-icons/fi";
import TestForPackage from "./testsByPackagePopup";
import "./hm-theme.css";

// ── Medical Abbreviation Dictionary ──
// Maps each abbreviation (lower-case) to an array of equivalent full-form keywords (lower-case).
// The lookup is fully bidirectional: full-form → abbreviation is also supported.
const MEDICAL_ABBREVIATIONS = {
  ecg:  ["electrocardiogram", "electrocardiography", "electro cardiogram", "12 lead ecg", "resting ecg"],
  ekg:  ["electrocardiogram", "electrocardiography"],
  cbc:  ["complete blood count", "complete blood picture", "complete blood cell", "full blood count"],
  cbp:  ["complete blood picture", "complete blood count"],
  lft:  ["liver function test", "liver function tests", "hepatic function"],
  kft:  ["kidney function test", "kidney function tests", "renal function test"],
  rft:  ["renal function test", "renal function tests", "kidney function test"],
  tft:  ["thyroid function test", "thyroid function tests", "thyroid profile"],
  pft:  ["pulmonary function test", "pulmonary function tests", "spirometry"],
  fbs:  ["fasting blood sugar", "fasting glucose", "fasting blood glucose"],
  ppbs: ["post prandial blood sugar", "postprandial blood sugar", "pp blood sugar", "post meal glucose"],
  rbs:  ["random blood sugar", "random glucose"],
  hba1c:["glycated haemoglobin", "glycated hemoglobin", "haemoglobin a1c", "hemoglobin a1c", "glycosylated haemoglobin"],
  bmi:  ["body mass index"],
  bp:   ["blood pressure"],
  cxr:  ["chest x-ray", "chest xray", "chest x ray", "chest radiograph"],
  usg:  ["ultrasonography", "ultrasound", "sonography"],
  mri:  ["magnetic resonance imaging"],
  ct:   ["computed tomography", "ct scan"],
  xray: ["x-ray", "x ray", "radiograph"],
  urine:["urine analysis", "urinalysis", "urine routine", "urine examination"],
  esr:  ["erythrocyte sedimentation rate", "sedimentation rate"],
  crp:  ["c-reactive protein", "c reactive protein"],
  ldl:  ["low density lipoprotein", "ldl cholesterol"],
  hdl:  ["high density lipoprotein", "hdl cholesterol"],
  tsh:  ["thyroid stimulating hormone", "thyrotropin"],
  t3:   ["triiodothyronine"],
  t4:   ["thyroxine", "tetraiodothyronine"],
  hb:   ["haemoglobin", "hemoglobin"],
  wbc:  ["white blood cells", "white blood count", "leucocyte count"],
  rbc:  ["red blood cells", "red blood count", "erythrocyte count"],
  plt:  ["platelet count", "thrombocyte count"],
  sgot: ["aspartate aminotransferase", "ast", "serum glutamic oxaloacetic transaminase"],
  sgpt: ["alanine aminotransferase", "alt", "serum glutamic pyruvic transaminase"],
  ggt:  ["gamma glutamyl transferase", "gamma-gt"],
  alp:  ["alkaline phosphatase"],
  bun:  ["blood urea nitrogen", "blood urea"],
  sr:   ["serum creatinine", "creatinine"],
  ua:   ["uric acid"],
  vdrl: ["venereal disease research laboratory", "syphilis test"],
  hiv:  ["human immunodeficiency virus", "hiv screening"],
  hbsag:["hepatitis b surface antigen", "hepatitis b"],
  hcv:  ["hepatitis c virus", "hepatitis c"],
  echocardiography: ["echo", "2d echo", "2d echocardiography", "cardiac echo"],
  echo: ["echocardiography", "2d echo", "2d echocardiography"],
  "2d echo": ["echocardiography", "echo", "2d echocardiography"],
  audiometry: ["hearing test", "audio test"],
  ophthalmic: ["eye test", "vision test", "ophthalmology"],
};

// Build a reverse map: full-form keyword → [abbreviation, ...]
const REVERSE_ABBR_MAP = {};
Object.entries(MEDICAL_ABBREVIATIONS).forEach(([abbr, fullForms]) => {
  fullForms.forEach((ff) => {
    if (!REVERSE_ABBR_MAP[ff]) REVERSE_ABBR_MAP[ff] = [];
    REVERSE_ABBR_MAP[ff].push(abbr);
  });
});

/**
 * Returns true if `testName` and `serviceName` are considered an abbreviation match.
 * Supports both directions: abbr→full and full→abbr.
 */
const isAbbreviationMatch = (testName, serviceName) => {
  const tn = testName.trim().toLowerCase();
  const sn = serviceName.trim().toLowerCase();

  // Direction 1: testName is an abbreviation, check if serviceName matches any full form
  if (MEDICAL_ABBREVIATIONS[tn]) {
    if (MEDICAL_ABBREVIATIONS[tn].some((ff) => sn === ff || sn.includes(ff) || ff.includes(sn))) return true;
  }
  // Direction 2: serviceName is an abbreviation, check if testName matches any full form
  if (MEDICAL_ABBREVIATIONS[sn]) {
    if (MEDICAL_ABBREVIATIONS[sn].some((ff) => tn === ff || tn.includes(ff) || ff.includes(tn))) return true;
  }
  // Direction 3: testName maps to abbreviation(s), check if serviceName matches those abbreviations
  if (REVERSE_ABBR_MAP[tn]) {
    if (REVERSE_ABBR_MAP[tn].some((abbr) => sn === abbr)) return true;
  }
  // Direction 4: serviceName maps to abbreviation(s), check if testName matches those abbreviations
  if (REVERSE_ABBR_MAP[sn]) {
    if (REVERSE_ABBR_MAP[sn].some((abbr) => tn === abbr)) return true;
  }
  return false;
};

function BookAnAppointment() {
  const { userid } = useParams();
  const [labs, setLabs] = useState([]);
  const [selectedLab, setSelectedLab] = useState(null);
  const [medallBranches, setMedallBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchLoader, setBranchLoader] = useState(false);
  const [branchSearchTerm, setBranchSearchTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [date, setDate] = useState("");
  const [step, setStep] = useState(1);
  const [loader, setLoader] = useState(true);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [companyRes, setCompanyRes] = useState(null);
  const [tests, setTests] = useState(null);
  const [employee, setEmployee] = useState("");
  const [packages, setPackages] = useState([]);
  const [designationMappings, setDesignationMappings] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [packageId, setPackageId] = useState("");
  const [packagePrice, setPackagePrice] = useState(0);
  const [selectedTests, setSelectedTests] = useState([]);
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [bmiScore, setBmiScore] = useState(null);
  const [apiRecommendedTests, setApiRecommendedTests] = useState([]);
  const navigate = useNavigate();
  const [isTestsModalOpen, setIsTestsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showTestDropdown, setShowTestDropdown] = useState(false);
  const [testSearchQuery, setTestSearchQuery] = useState("");
  const testPickerRef = useRef(null);

  // Close the Additional Investigations dropdown (and clear its search) on outside click
  useEffect(() => {
    if (!showTestDropdown) return;
    const handleOutsideClick = (e) => {
      if (testPickerRef.current && !testPickerRef.current.contains(e.target)) {
        setShowTestDropdown(false);
        setTestSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showTestDropdown]);
  const [homeCollection, setHomeCollection] = useState(false);
  const [packageTests, setPackageTests] = useState([]);
  const [homeCollectionAvailable, setHomeCollectionAvailable] = useState(null); // null = not yet determined
  const [bookingCategories, setBookingCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedPackageType, setSelectedPackageType] = useState("Initial");
  const [medallServiceList, setMedallServiceList] = useState([]);
  const [serviceLoader, setServiceLoader] = useState(false);
  // Keys of services the user has explicitly unchecked (everything else in the list counts as selected,
  // so newly fetched services default to selected — matching the previous all-checked appearance)
  const [unselectedMedallServiceKeys, setUnselectedMedallServiceKeys] = useState([]);
  // Exact-match selection modal state
  const [exactMatchModalOpen, setExactMatchModalOpen] = useState(false);
  const [exactMatchGroups, setExactMatchGroups] = useState([]); // [{testName, matches:[svc,...]}, ...]
  const [exactMatchSelections, setExactMatchSelections] = useState({}); // { testName: svc }
  const [pendingMergedList, setPendingMergedList] = useState([]);
  const userRole = JSON.parse(localStorage.getItem("data")).role;
  const token = JSON.parse(localStorage.getItem("data")).token;

  const isMedallLab = (lab) => {
    if (!lab) return false;
    const name = (lab.labname || lab.name || "").trim().toLowerCase();
    return name.startsWith("medall");
  };

  // Stable key for a Medall service — mirrors the dedupe key used when merging the fetched list
  const getServiceKey = (svc, idx) =>
    svc?.ServiceId ?? svc?.ServiceCode ?? svc?.ServiceName ?? svc?.serviceName ?? `idx-${idx}`;

  // Derived: services currently selected (all minus explicitly unchecked ones)
  const selectedMedallServices = medallServiceList.filter(
    (svc, idx) => !unselectedMedallServiceKeys.some((k) => k === String(getServiceKey(svc, idx)))
  );

  // Max selectable count: total minus related siblings that must stay unchecked
  // (only the first related match per queried test can be selected at once).
  const maxSelectableMedallServices = (() => {
    const seenRelatedQueries = new Set();
    let blocked = 0;
    medallServiceList.forEach((svc) => {
      if (svc._matchType === "related" && svc._queryName) {
        if (seenRelatedQueries.has(svc._queryName)) blocked += 1;
        else seenRelatedQueries.add(svc._queryName);
      }
    });
    return medallServiceList.length - blocked;
  })();

  const allMedallServicesSelected =
    medallServiceList.length > 0 &&
    selectedMedallServices.length >= maxSelectableMedallServices;

  const handleMedallServiceToggle = (svcKey, isChecked, svc) => {
    const key = String(svcKey);
    setUnselectedMedallServiceKeys((prev) => {
      // Start from the plain add/remove of the toggled key
      let next = isChecked
        ? prev.filter((k) => k !== key)
        : prev.includes(key)
          ? prev
          : [...prev, key];

      // Related-match exclusivity: when checking a "Related" service, uncheck the
      // other related services that were found for the SAME queried test name,
      // so only one related pick per queried test is allowed.
      if (isChecked && svc?._matchType === "related" && svc?._queryName) {
        const siblingKeys = medallServiceList
          .map((s, i) => ({ s, i }))
          .filter(
            ({ s, i }) =>
              s !== svc &&
              s._matchType === "related" &&
              s._queryName === svc._queryName &&
              !next.includes(String(getServiceKey(s, i)))
          )
          .map(({ s, i }) => String(getServiceKey(s, i)));
        // Add siblings to the unselected set (they were checked, now become unchecked)
        next = [...next, ...siblingKeys.filter((k) => !next.includes(k))];
      }
      return next;
    });
  };

  const handleToggleAllMedallServices = (isChecked) => {
    if (!isChecked) {
      // Uncheck everything
      setUnselectedMedallServiceKeys(
        medallServiceList.map((svc, idx) => String(getServiceKey(svc, idx)))
      );
      return;
    }
    // Check everything, but enforce Related-match exclusivity:
    // for each queried test that only has related matches, keep just the first one checked.
    const unselected = [];
    const seenRelatedQueries = new Set();
    medallServiceList.forEach((svc, idx) => {
      if (svc._matchType === "related" && svc._queryName) {
        if (seenRelatedQueries.has(svc._queryName)) {
          unselected.push(String(getServiceKey(svc, idx)));
        } else {
          seenRelatedQueries.add(svc._queryName);
        }
      }
    });
    setUnselectedMedallServiceKeys(unselected);
  };




  // Guards to prevent duplicate concurrent calls
  const fetchingBranchesRef = React.useRef(false);
  const fetchingServiceRef = React.useRef(false);

  const fetchMedallBranches = async () => {
    if (!selectedLab?.id) return;
    if (fetchingBranchesRef.current) return;
    fetchingBranchesRef.current = true;
    setBranchLoader(true);
    try {
      // Backend resolves labCorporateId, API URL, and auth credentials from DB
      const response = await axios.get(
        `/api/third-party-lab/${selectedLab.id}/branches`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const list = Array.isArray(response.data) ? response.data : [];

      setMedallBranches(list);
      if (list.length > 0) {
        setSelectedBranch(list[0]);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
      toast.error("Failed to load branches.");
    } finally {
      setBranchLoader(false);
      fetchingBranchesRef.current = false;
    }
  };

  useEffect(() => {
    if (isMedallLab(selectedLab)) {
      fetchMedallBranches();
    } else {
      setMedallBranches([]);
      setSelectedBranch(null);
      setMedallServiceList([]);
    }
  }, [selectedLab]);

  // Fetch Medall service list — triggered when branch, package, or additional tests change
  const fetchMedallServiceList = async (branchId, currentPackageTests, additionalTests) => {
    if (!branchId) return;
    if (fetchingServiceRef.current) return; // already in-flight, skip duplicate

    // Collect test names from package tests
    const pkgTestNames = (currentPackageTests || [])
      .map((t) => t.lab?.testname || t.labTest?.testname || t.testname || "")
      .filter(Boolean);

    // Collect test names from additional (manually selected) tests
    const addlTestNames = (additionalTests || [])
      .map((st) => {
        const fullTest = labTests.find((lt) => Number(lt.id) === Number(st.testId));
        return fullTest?.testname || "";
      })
      .filter(Boolean);

    // Merge and deduplicate test names
    const testNames = [...new Set([...pkgTestNames, ...addlTestNames])];

    if (testNames.length === 0) {
      setMedallServiceList([]);
      return;
    }

    fetchingServiceRef.current = true;
    setServiceLoader(true);
    try {
      // Backend resolves API URL, stored request body template, and auth credentials from DB.
      // Calls the third-party API once per test name and returns aggregated results.
      const response = await axios.post(
        `/api/third-party-lab/${selectedLab.id}/services`,
        { branchId: String(branchId), testNames },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const flatResults = Array.isArray(response.data) ? response.data : [];

      // Re-shape into per-test-name batches so the existing smart-match logic below works unchanged.
      // Backend returns a flat aggregated array; split it back by testName field for matching.
      const results = testNames.map((testName) => {
        const nameLower = testName.trim().toLowerCase();
        // Services whose ServiceName contains the queried test name belong to this batch
        const batch = flatResults.filter((svc) => {
          const svcName = (svc.ServiceName || svc.serviceName || svc.Name || "").trim().toLowerCase();
          return svcName.includes(nameLower) || nameLower.includes(svcName);
        });
        // Fallback: include all if nothing matched (avoids empty batches losing results)
        return batch.length > 0 ? batch : flatResults;
      });

      // ── Smart-match logic (Exact → Abbreviation → Related) ──
      // For each queried test name, classify each returned service:
      //   "exact"       – case-insensitive identical name
      //   "abbreviation" – one side is the known abbreviation of the other (e.g. ECG ↔ Electrocardiogram)
      //   "related"     – partial/keyword match returned by the Medall API
      // Priority rules:
      //   • If ≥1 exact match exists → only exact matches are shown for that test
      //   • Else if ≥1 abbreviation match → only abbreviation matches shown
      //   • Else → all partial (related) results shown
      //   • Multiple exact matches for the same test → disambiguation modal
      const exactMatchGroupsFound = [];
      const seen = new Set();
      const merged = [];

      const pushSvc = (svc, matchType, queryName) => {
        const key = svc.ServiceId ?? svc.ServiceCode ?? svc.ServiceName ?? svc.serviceName;
        if (key !== undefined && key !== null && !seen.has(key)) {
          seen.add(key);
          merged.push({ ...svc, _matchType: matchType, _queryName: queryName ?? null });
        } else if (key === undefined || key === null) {
          merged.push({ ...svc, _matchType: matchType, _queryName: queryName ?? null });
        }
      };

      results.forEach((batch, i) => {
        const queriedName = testNames[i].trim().toLowerCase();

        const exactMatches = batch.filter(
          (svc) =>
            (svc.ServiceName || svc.serviceName || svc.Name || "").trim().toLowerCase() === queriedName
        );

        const abbrMatches = batch.filter(
          (svc) =>
            !exactMatches.includes(svc) &&
            isAbbreviationMatch(
              queriedName,
              (svc.ServiceName || svc.serviceName || svc.Name || "").trim().toLowerCase()
            )
        );

        if (exactMatches.length === 1) {
          // Single exact match — auto-select it, exclude everything else for this test
          pushSvc(exactMatches[0], "exact", queriedName);
        } else if (exactMatches.length > 1) {
          // Multiple exact matches — open selection modal
          exactMatchGroupsFound.push({ testName: testNames[i], matches: exactMatches });
          exactMatches.forEach((svc) => pushSvc(svc, "exact", queriedName));
        } else if (abbrMatches.length > 0) {
          // No exact, but abbreviation matches found
          abbrMatches.forEach((svc) => pushSvc(svc, "abbreviation", queriedName));
        } else {
          // No exact or abbreviation match — show all partial (related) results
          batch.forEach((svc) => pushSvc(svc, "related", queriedName));
        }
      });

      // Auto-uncheck non-exact services: only "exact" matches are pre-selected by default.
      // Abbreviation and Related matches are shown but unchecked — user opts in.
      const nonExactKeys = merged
        .filter((svc) => svc._matchType !== "exact")
        .map((svc) => String(svc.ServiceId ?? svc.ServiceCode ?? svc.ServiceName ?? svc.serviceName ?? ""))
        .filter(Boolean);
      // Reset, then apply: exact matches stay checked, everything else starts unchecked
      setUnselectedMedallServiceKeys(nonExactKeys);

      if (exactMatchGroupsFound.length > 0) {
        // Prompt user to disambiguate
        setExactMatchGroups(exactMatchGroupsFound);
        setExactMatchSelections({});
        setPendingMergedList(merged);
        setMedallServiceList(merged);
        setExactMatchModalOpen(true);
      } else {
        setMedallServiceList(merged);
      }
    } catch (error) {
      console.error("Error fetching Medall service list:", error);
      toast.error("Failed to load Medall service list.");
    } finally {
      setServiceLoader(false);
      fetchingServiceRef.current = false;
    }
  };

  // Re-fetch service list when branch changes — if a package or tests are already selected
  useEffect(() => {
    setUnselectedMedallServiceKeys([]); // reset service selections when switching branches
    if (isMedallLab(selectedLab) && selectedBranch?.BranchId && (packageTests.length > 0 || selectedTests.length > 0)) {
      fetchMedallServiceList(selectedBranch.BranchId, packageTests, selectedTests);
    } else {
      setMedallServiceList([]);
    }
  }, [selectedBranch]);

  // Re-fetch service list when additional tests change
  useEffect(() => {
    if (isMedallLab(selectedLab) && selectedBranch?.BranchId && (packageTests.length > 0 || selectedTests.length > 0)) {
      const timeout = setTimeout(() => {
        fetchMedallServiceList(selectedBranch.BranchId, packageTests, selectedTests);
      }, 300); // debounce to avoid rapid API calls when toggling multiple tests
      return () => clearTimeout(timeout);
    }
  }, [selectedTests]);

  useEffect(() => {
    if (searchTerm.trim()) {
      searchData(0);
    } else {
      getData(currentPage);
    }
  }, [searchTerm]);

  const searchData = async (pageNumber = 0) => {
    setLoader(true);
    try {
      const searchUrl = `/api/labdetails/searchLabsForAppointment/regulated=true/empUserid=${userid}`;
      const requestData = { searchTerm };
      const response = await axios.post(searchUrl, requestData, {
        params: { page: pageNumber },
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabs(response.data.content);
      setCurrentPage(response.data.number);
      setTotalPages(response.data.totalPages);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const getData = async (pageNumber = 0) => {
    setLoader(true);
    try {
      const trans = await axios.get(
        `/api/labdetails/getAllLabsForAppointment/regulated=true/empUserid=${userid}`,
        {
          params: { page: pageNumber },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setLabs(trans.data.content);
      setCurrentPage(trans.data.number);
      setTotalPages(trans.data.totalPages);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const fetchData = async () => {
    setLoader(true);
    try {
      const empRes = await axios.get(`/api/employeedetails/userid=${userid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployee(empRes.data);

      const compRes = await axios.get(
        `/api/companydetails/${empRes.data?.companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanyRes(compRes.data);

      if (compRes.data?.packageByCategory === true) {
        try {
          const catRes = await axios.get(
            `/api/bookingcategory/companyId=${empRes.data?.companyId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const cats = catRes.data || [];
          setBookingCategories(cats);
          if (cats.length > 0) {
            setSelectedCategoryId(cats[0].id);
          }
        } catch (_) { }
      }

      // Recommendation logic is delegated to the /byRules endpoint

      const testRes = await axios.get(`/api/labtest`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabTests(testRes.data);

      if (
        compRes.data.packageByAge === false &&
        compRes.data.restrictedPkgByDesignation === false
      ) {
        const pkgResponse = await axios.get(
          `/api/package/companyId=${empRes.data?.companyId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPackages(pkgResponse.data || []);
        setDesignationMappings([]);
      } else {
        const response = await axios.get(
          `/api/packagedesignationmapping/employee=${userid}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data && response.data.length > 0) {
          setDesignationMappings(response.data);
          const mappedPackages = response.data.map(m => m.pac).filter(Boolean);
          setPackages(mappedPackages);
          if (response.data.length === 1) {
            const mapping = response.data[0];
            setTests(mapping.pac?.packagename);
            setPackageId(mapping.packageid);
            setPackagePrice(mapping.pac?.price || 0);
            // Fetch labMandatory info for auto-selected package
            try {
              const mappingRes = await axios.get(
                `/api/packagetestmapping/packageid=${mapping.packageid}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              const pkgTestList = mappingRes.data || [];
              setPackageTests(pkgTestList);
              if (compRes.data?.homeCollection === true) {
                const anyLabMandatory = pkgTestList.some(t => t.lab?.labMandatory === true);
                setHomeCollectionAvailable(!anyLabMandatory);
              }
            } catch (_) { }
          } else {
            // Multiple designation-mapped packages — let user choose via dropdown
            setTests(null);
            setPackageId("");
            setPackagePrice(0);
          }
        } else {
          try {
            const pkgResponse = await axios.get(
              `/api/package/companyId=${empRes.data?.companyId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setPackages(pkgResponse.data || []);
          } catch (_) {
            setPackages([]);
          }
          setDesignationMappings([]);
          setTests(null);
          setPackageId("");
          setPackagePrice(0);
        }
      }

      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  function handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else if (error.response.data && error.response.data.code) {
        toast.error(error.response.data.code);
      } else {
        toast.error("An error occurred.");
      }
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error("An error occurred.");
    }
  }

  useEffect(() => {
    getData();
    fetchData();
  }, []);

  // Helper to compute age from DOB string (YYYY-MM-DD)
  const computeAge = (dob) => {
    if (!dob) return null;
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const fetchRecommendationsByRules = async (bmiVal, ageVal) => {
    if (!companyRes || !companyRes.id) return;
    try {
      const token = JSON.parse(localStorage.getItem("data")).token;
      const requestData = {
        companyId: companyRes.id,
        age: ageVal,
        bmi: companyRes.bmiCalculator ? bmiVal : null,
      };

      console.log("Calling byRules API with request body:", requestData);
      const resp = await axios.post(`/api/recommendationruletests/byRules`, requestData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("byRules API response:", resp.data);
      setApiRecommendedTests(resp.data.recommendedTests || []);
    } catch (e) {
      console.error("Error fetching recommended tests by rules", e);
    }
  };

  // Trigger recommendation fetching when employee, company settings, or weight/height inputs change
  useEffect(() => {
    if (employee && companyRes && companyRes.id) {
      const age = computeAge(employee.dob);
      let bmi = null;
      if (companyRes.bmiCalculator && weight && height) {
        const hInMeters = parseFloat(height) / 100;
        const wInKg = parseFloat(weight);
        if (hInMeters > 0 && wInKg > 0 && weight.toString().length > 1 && height.toString().length > 1) {
          bmi = parseFloat((wInKg / (hInMeters * hInMeters)).toFixed(2));
          setBmiScore(bmi);
        } else {
          setBmiScore(null);
        }
      } else {
        setBmiScore(null);
      }

      const timeout = setTimeout(() => {
        fetchRecommendationsByRules(bmi, age);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [employee, companyRes, weight, height]);

  // Map API recommended tests to full labTest objects from the catalog
  const combinedRecommendedTests = apiRecommendedTests
    .map((rt) => {
      const fullTest = labTests.find((lt) => lt.id === Number(rt.testId));
      if (fullTest) {
        return {
          ...fullTest,
          ruleName: rt.ruleName,
        };
      }
      return null;
    })
    .filter(Boolean);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbersToShow = 5;
    let startPage = Math.max(0, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages - 1, maxPageNumbersToShow - 1);
    }

    if (currentPage >= totalPages - 3) {
      startPage = Math.max(0, totalPages - maxPageNumbersToShow);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (searchTerm.trim()) {
      searchData(newPage);
    } else {
      getData(newPage);
    }
  };

  const handleBookAppointment = async () => {
    if (isMedallLab(selectedLab) && !selectedBranch) {
      toast.warn("Please select a Medall branch");
      return;
    }

    if (!date && packageId) {
      toast.warn("Please select a date");
      return;
    }
    if (!packageId && date) {
      toast.warn("Please select a package");
      return;
    }
    if (!packageId && !date) {
      toast.warn("Please select a date and package");
      return;
    }

    const selectedDate = new Date(date);
    const currentDate = new Date();
    const oneDayFromNow = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate() + 1
    );

    if (userRole !== "ROLE_SUPER" && userRole !== "ROLE_ADMIN") {
      if (selectedDate < oneDayFromNow || selectedDate.getDay() === 0) {
        toast.warn(
          "Please select a weekday other than Sunday and ensure the date is at least 1 day from today."
        );
        return;
      }
    }

    if (selectedDate.getDay() === 0) {
      toast.warn("Please select a weekday other than Sunday.");
      return;
    }

    setShowConfirmationModal(true);
  };

  const handleShowConfirmationModal = async () => {
    setShowConfirmationModal(true);
  };

  const confirmBookAppointment = async () => {
    if (companyRes?.requireEmpPayment === true) {
      const medallServiceCodes =
        isMedallLab(selectedLab) && selectedMedallServices.length > 0
          ? selectedMedallServices
              .map((svc) => svc.ServiceCode || svc.serviceCode)
              .filter(Boolean)
          : null;

      const data = {
        packageId: packageId || null,
        selectedTests: selectedTests.length > 0 ? selectedTests : null,
        selectedMedallServices:
          isMedallLab(selectedLab) && selectedMedallServices.length > 0
            ? selectedMedallServices.map((svc) => ({
              serviceId: svc.ServiceId ?? null,
              serviceName: svc.ServiceName || svc.serviceName || svc.Name || null,
              serviceCode: svc.ServiceCode ?? null,
              rate: svc.Rate ?? null,
            }))
            : null,
        serviceCodes: medallServiceCodes,
        userid: userid,
        labId: selectedLab.id,
        appointmentDate: date,
        homeCollection,
        branchId: selectedBranch?.BranchId || null,
        branchCode: selectedBranch?.BranchCode || null,
        branchName: selectedBranch?.BranchName || null,
        branchAddress: selectedBranch?.Address || null,
      };
      navigate("/users/razorpay", { state: data });
    } else {
      try {
        setLoader(true);
        const medallServiceCodes =
          isMedallLab(selectedLab) && selectedMedallServices.length > 0
            ? selectedMedallServices
                .map((svc) => svc.ServiceCode || svc.serviceCode)
                .filter(Boolean)
            : null;

        await axios.post(
          "/api/employeeappointment/create",
          {
            userid: userid,
            labId: selectedLab.id,
            date,
            packageid: packageId,
            additionalTest: selectedTests,
            selectedMedallServices:
              isMedallLab(selectedLab) && selectedMedallServices.length > 0
                ? selectedMedallServices.map((svc) => ({
                  serviceId: svc.ServiceId ?? null,
                  serviceName: svc.ServiceName || svc.serviceName || svc.Name || null,
                  serviceCode: svc.ServiceCode ?? null,
                  rate: svc.Rate ?? null,
                }))
                : null,
            serviceCodes: medallServiceCodes,
            homeCollection,
            branchId: selectedBranch?.BranchId || null,
            branchCode: selectedBranch?.BranchCode || null,
            branchName: selectedBranch?.BranchName || null,
            branchAddress: selectedBranch?.Address || null,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success("Appointment Booked successfully");
        if (userRole === "ROLE_EMPLOYEE") {
          navigate("/users/employee-appointments");
        } else {
          navigate(`/users/employee-profile/${userid}`);
        }
      } catch (error) {
        setLoader(false);
        handleError(error);
      } finally {
        setShowConfirmationModal(false);
      }
    }
  };

  const handlePackageSelection = async (e) => {
    const selectedPackageId = e.target.value;
    setPackageId(selectedPackageId);
    setHomeCollection(false);
    setHomeCollectionAvailable(null);
    setPackageTests([]);
    setUnselectedMedallServiceKeys([]); // reset service selections when switching packages

    if (!selectedPackageId) {
      setTests(null);
      setPackagePrice(0);
      return;
    }

    try {
      setLoader(true);
      const [pkgRes, mappingRes] = await Promise.all([
        axios.get(`/api/package/${selectedPackageId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`/api/packagetestmapping/packageid=${selectedPackageId}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      // Resolve name/price from designation mappings first, else from API
      if (designationMappings.length > 0) {
        const mapping = designationMappings.find(
          (m) => String(m.packageid) === String(selectedPackageId)
        );
        if (mapping?.pac) {
          setTests(mapping.pac.packagename);
          setPackagePrice(mapping.pac.price || 0);
        } else {
          setTests(pkgRes.data.packagename);
          setPackagePrice(pkgRes.data.price || 0);
        }
      } else {
        setTests(pkgRes.data.packagename);
        setPackagePrice(pkgRes.data.price || 0);
      }

      const pkgTestList = mappingRes.data || [];
      setPackageTests(pkgTestList);

      // Deselect any optional tests that are already included in the selected package
      setSelectedTests((prev) =>
        prev.filter((st) => {
          return !pkgTestList.some((pt) => {
            const pkgTestId = pt.lab?.id || pt.labTest?.id || pt.testid || pt.id;
            const pkgTestName = (pt.lab?.testname || pt.labTest?.testname || pt.testname || "").toLowerCase().trim();
            const selectedTestObj = labTests.find((lt) => Number(lt.id) === Number(st.testId));
            const selectedTestName = (selectedTestObj?.testname || "").toLowerCase().trim();
            return (
              (pkgTestId && Number(pkgTestId) === Number(st.testId)) ||
              (pkgTestName && selectedTestName && pkgTestName === selectedTestName)
            );
          });
        })
      );

      if (companyRes?.homeCollection === true) {
        const anyLabMandatory = pkgTestList.some(t => t.lab?.labMandatory === true);
        setHomeCollectionAvailable(!anyLabMandatory);
      }

      // If a Medall branch is selected, fetch service list with the new package's tests + any additional tests
      if (isMedallLab(selectedLab) && selectedBranch?.BranchId) {
        fetchMedallServiceList(selectedBranch.BranchId, pkgTestList, selectedTests);
      }

      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  // ✅ Handle adding new tests
  const handleTestCheckboxChange = (testIdStr, isChecked) => {
    if (isChecked) {
      if (!selectedTests.some(t => t.testId === Number(testIdStr))) {
        const rec = apiRecommendedTests.find((rt) => Number(rt.testId) === Number(testIdStr));
        const testSource = rec ? "Recommended" : "Manual";
        const ruleId = rec ? rec.ruleId : null;
        const ruleName = rec ? rec.ruleName : null;
        setSelectedTests((prev) => [...prev, { testId: Number(testIdStr), testSource, ruleId, ruleName }]);
      }
    } else {
      setSelectedTests((prev) => prev.filter((t) => t.testId !== Number(testIdStr)));
    }
  };

  // ✅ Remove selected test
  const handleRemoveTest = (id) => {
    setSelectedTests((prev) => prev.filter((t) => t.testId !== Number(id)));
  };

  const openTestsModal = (pkg) => {
    setSelectedPackage(pkg);
    setIsTestsModalOpen(true);
  };

  const closeTestsModal = () => {
    setSelectedPackage(null);
    setIsTestsModalOpen(false);
  };

  const handleUseMyLocation = async (pageNumber = 0) => {
    setLoader(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await axios.get(`/api/labdetails/nearByLocation`, {
              params: {
                latitude,
                longitude,
                radius: 10,
                empUserid: userid,
                page: pageNumber,
              },
              headers: { Authorization: `Bearer ${token}` },
            });

            setLabs(response.data.content);
            setCurrentPage(response.data.number);
            setTotalPages(response.data.totalPages);
            setLoader(false);
          } catch (error) {
            setLoader(false);
            handleError(error);
          }
        },
        (error) => {
          handleError(error);
          setLoader(false);
        }
      );
    } else {
      setLoader(false);
      toast.error("Geolocation is not supported by this browser.");
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 1:
        return (
          <div className="hm-card mb-4">
            <div className="hm-card-header d-flex align-items-center justify-content-between flex-wrap gap-3">
              <h3 className="hm-card-title m-0">
                <i className="material-icons">domain</i>
                Choose Lab
              </h3>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div className="position-relative" style={{ width: "280px" }}>
                  <input
                    type="search"
                    className="form-control ps-5"
                    placeholder="Search lab, code, city, pincode"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="position-absolute text-muted"
                    style={{ left: "14px", top: "50%", transform: "translateY(-50%)" }}
                  />
                </div>
                <Button
                  variant="outline-primary"
                  className="rounded-pill btn-sm d-flex align-items-center gap-1 px-3 py-2"
                  onClick={() => handleUseMyLocation(0)}
                >
                  <FontAwesomeIcon icon={faMapMarkerAlt} /> Use My Location
                </Button>
              </div>
            </div>

            <div className="hm-card-body p-4">
              <div className="row g-4 mb-4">
                {labs.length === 0 ? (
                  <div className="col-12">
                    <div className="hm-empty-state py-5">
                      <i className="material-icons">search_off</i>
                      <p>No Diagnostic Labs Found</p>
                    </div>
                  </div>
                ) : (
                  labs.map((lab) => {
                    const isSelected = selectedLab && selectedLab.id === lab.id;
                    return (
                      <div className="col-md-6 col-lg-4" key={lab.id}>
                        <div
                          className={`hm-card h-100 p-0 border transition-all cursor-pointer ${isSelected
                              ? "border-primary shadow"
                              : "border-light shadow-sm hover-shadow"
                            }`}
                          onClick={() => setSelectedLab(lab)}
                          style={{
                            cursor: "pointer",
                            transition: "all 0.2s ease-in-out",
                            borderColor: isSelected ? "#2f3a88" : undefined,
                            borderWidth: isSelected ? "2px" : "1px"
                          }}
                        >
                          {lab.image && (
                            <img
                              src={lab.image}
                              className="card-img-top"
                              alt={lab.labname}
                              style={{ height: "140px", objectFit: "cover" }}
                            />
                          )}
                          <div className="p-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <h5 className="fw-bold mb-0 text-truncate" style={{ color: "#1e293b", fontSize: "1rem" }}>
                                {lab.labname}
                              </h5>
                              <span className="badge bg-primary bg-opacity-10 text-primary small">
                                {lab.labcode}
                              </span>
                            </div>
                            <p className="text-muted small mb-1">
                              <i className="material-icons align-middle me-1" style={{ fontSize: "0.9rem" }}>phone</i>
                              {lab.contact}
                            </p>
                            <p className="text-muted small mb-0">
                              <i className="material-icons align-middle me-1" style={{ fontSize: "0.9rem" }}>location_on</i>
                              {lab.addressLine1}, {lab.city}, {lab.pincode}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* ── Medall Diagnostic Branches Display ── */}
              {isMedallLab(selectedLab) && (
                <div className="medall-branch-container mb-4">
                  <div className="medall-branch-header">
                    <div>
                      <h4 className="medall-branch-title">
                        <i className="material-icons text-primary">storefront</i>
                        Medall Diagnostic Branches
                      </h4>
                      <p className="text-muted small mb-0">
                        Selected Lab: <strong>{selectedLab.labname}</strong> &mdash; Choose your branch location below:
                      </p>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <div className="position-relative" style={{ width: "260px" }}>
                        <input
                          type="search"
                          className="form-control form-control-sm ps-4"
                          placeholder="Search branch, city, pincode..."
                          value={branchSearchTerm}
                          onChange={(e) => setBranchSearchTerm(e.target.value)}
                        />
                        <FontAwesomeIcon
                          icon={faSearch}
                          className="position-absolute text-muted"
                          style={{ left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "11px" }}
                        />
                      </div>
                      <span className="badge bg-primary bg-opacity-10 text-primary small py-2 px-3 rounded-pill">
                        {medallBranches.length} Branches Available
                      </span>
                    </div>
                  </div>

                  {branchLoader ? (
                    <div className="d-flex align-items-center justify-content-center py-4 gap-2 text-primary">
                      <div className="spinner-border spinner-border-sm" role="status"></div>
                      <span className="small fw-semibold">Loading Medall branch list...</span>
                    </div>
                  ) : (
                    (() => {
                      const filtered = medallBranches.filter((b) => {
                        if (!branchSearchTerm.trim()) return true;
                        const term = branchSearchTerm.toLowerCase();
                        return (
                          (b.BranchName || "").toLowerCase().includes(term) ||
                          (b.BranchCode || "").toLowerCase().includes(term) ||
                          (b.CityName || "").toLowerCase().includes(term) ||
                          (b.Address || "").toLowerCase().includes(term) ||
                          (b.Pincode || "").toLowerCase().includes(term)
                        );
                      });

                      if (filtered.length === 0) {
                        return (
                          <div className="text-center py-4 text-muted small">
                            <i className="material-icons d-block mb-1" style={{ fontSize: "2rem" }}>search_off</i>
                            No branches found matching "{branchSearchTerm}"
                          </div>
                        );
                      }

                      return (
                        <div className="medall-branch-grid">
                          {filtered.map((branch) => {
                            const isBranchSelected =
                              selectedBranch && String(selectedBranch.BranchId) === String(branch.BranchId);
                            return (
                              <div
                                key={branch.BranchId}
                                className={`medall-branch-card ${isBranchSelected ? "selected" : ""}`}
                                onClick={() => setSelectedBranch(branch)}
                              >
                                <div>
                                  <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                                    <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "0.95rem" }}>
                                      {branch.BranchName}
                                    </h6>
                                    <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                      {branch.BranchClassificationName && (
                                        <span className="badge bg-secondary bg-opacity-10 text-secondary small py-1 px-2 rounded-pill" style={{ fontSize: "0.7rem" }}>
                                          {branch.BranchClassificationName}
                                        </span>
                                      )}
                                      <span className="medall-branch-badge">
                                        Code: {branch.BranchCode}
                                      </span>
                                    </div>
                                  </div>

                                  <p className="text-muted small mb-1" style={{ fontSize: "0.82rem", lineHeight: "1.4" }}>
                                    <i className="material-icons align-middle text-primary me-1" style={{ fontSize: "0.95rem" }}>location_on</i>
                                    {branch.Address}
                                  </p>

                                  <div className="d-flex align-items-center gap-3 text-muted small mt-2" style={{ fontSize: "0.78rem" }}>
                                    {branch.CityName && (
                                      <span>
                                        <strong className="text-dark">City:</strong> {branch.CityName}
                                      </span>
                                    )}
                                    {branch.Pincode && (
                                      <span>
                                        <strong className="text-dark">PIN:</strong> {branch.Pincode}
                                      </span>
                                    )}
                                  </div>

                                  {branch.ContactNo && (
                                    <div className="text-muted small mt-1" style={{ fontSize: "0.78rem" }}>
                                      <i className="material-icons align-middle text-muted me-1" style={{ fontSize: "0.85rem" }}>phone</i>
                                      {branch.ContactNo}
                                    </div>
                                  )}
                                </div>

                                <div className="mt-3 pt-2 border-top d-flex align-items-center justify-content-between">
                                  <span className="small fw-semibold" style={{ color: isBranchSelected ? "#2563eb" : "#64748b" }}>
                                    {isBranchSelected ? "✓ Selected Branch" : "Click to select"}
                                  </span>
                                  <button
                                    type="button"
                                    className={`btn btn-sm rounded-pill px-3 ${isBranchSelected ? "btn-primary shadow-sm" : "btn-outline-primary"
                                      }`}
                                    style={{ fontSize: "0.75rem", pointerEvents: "none" }}
                                  >
                                    {isBranchSelected ? "Selected" : "Select"}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                  )}
                </div>
              )}

              <div className="d-flex justify-content-center mb-4">
                <Pagination>
                  <Pagination.First
                    onClick={() => handlePageChange(0)}
                    disabled={currentPage === 0}
                  />
                  <Pagination.Prev
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 0}
                  />
                  {renderPageNumbers()}
                  <Pagination.Next
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages - 1}
                  />
                  <Pagination.Last
                    onClick={() => handlePageChange(totalPages - 1)}
                    disabled={currentPage === totalPages - 1}
                  />
                </Pagination>
              </div>

              <div className="d-flex justify-content-end pt-3 border-top">
                <button
                  className="btn-hm-primary px-4"
                  onClick={() => {
                    if (isMedallLab(selectedLab) && !selectedBranch) {
                      toast.warn("Please select a Medall branch to continue.");
                      return;
                    }
                    setStep(2);
                  }}
                  disabled={!selectedLab || (isMedallLab(selectedLab) && !selectedBranch)}
                >
                  Next Step ➔
                </button>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="hm-card mb-4 overflow-hidden" style={{ borderRadius: "20px" }}>
            {/* ── Premium Hero Header Banner ── */}
            <div className="bcat-hero-banner">
              <div className="bcat-hero-overlay">
                <div className="bcat-hero-title-row">
                  <div>
                    <span className="bcat-hero-badge">
                      <i className="material-icons" style={{ fontSize: "0.85rem" }}>medical_services</i>
                      Medical Appointment Booking
                    </span>
                    <h3 className="bcat-hero-title">
                      Book Medical Examination
                    </h3>
                    <p className="bcat-hero-subtitle">
                      Configure your category, medical examination type, schedule date, and review investigations.
                    </p>
                  </div>
                </div>

                <div className="bcat-hero-chips">
                  {selectedLab && (
                    <div className="bcat-hero-chip">
                      <i className="material-icons">local_hospital</i>
                      <span>Lab: <strong>{selectedLab.labname}</strong></span>
                    </div>
                  )}
                  {selectedBranch && (
                    <div className="bcat-hero-chip">
                      <i className="material-icons">storefront</i>
                      <span>Branch: <strong>{selectedBranch.BranchName}</strong></span>
                    </div>
                  )}
                  {employee?.name && (
                    <div className="bcat-hero-chip">
                      <i className="material-icons">person</i>
                      <span>Candidate: <strong>{employee.name}</strong></span>
                    </div>
                  )}
                  {companyRes?.companyname && (
                    <div className="bcat-hero-chip">
                      <i className="material-icons">corporate_fare</i>
                      <span>Org: <strong>{companyRes.companyname}</strong></span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="hm-card-body p-4">
              {selectedLab && (
                <div className="row g-4">
                  {/* Left Column: Category & Package Selection */}
                  <div className="col-lg-6 border-end pe-lg-4">
                    {/* Glass summary card showing selected lab and current package status */}
                    <div className="bcat-glass-summary">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <i className="material-icons text-primary" style={{ fontSize: "1.3rem" }}>business</i>
                          <div>
                            <span className="fw-bold text-dark d-block">{selectedLab.labname}</span>
                            {selectedBranch && (
                              <span className="small text-muted d-block" style={{ fontSize: "0.8rem" }}>
                                Branch: <strong className="text-primary">{selectedBranch.BranchName}</strong> ({selectedBranch.BranchCode})
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="badge bg-primary bg-opacity-10 text-primary px-2 py-1 rounded-pill small">
                          Selected Center
                        </span>
                      </div>
                      {selectedBranch && (
                        <div className="small text-muted mb-2 ps-4" style={{ fontSize: "0.78rem" }}>
                          <i className="material-icons align-middle me-1" style={{ fontSize: "0.85rem" }}>location_on</i>
                          {selectedBranch.Address}
                          {selectedBranch.CityName ? `, ${selectedBranch.CityName}` : ""}
                          {selectedBranch.Pincode ? ` - ${selectedBranch.Pincode}` : ""}
                        </div>
                      )}
                      {tests && (
                        <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                          <span className="small text-muted">Selected Package: <strong className="text-dark">{tests}</strong></span>
                          <FiInfo
                            onClick={() => openTestsModal(packageId)}
                            className="text-primary ms-2 cursor-pointer"
                            style={{ cursor: "pointer" }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Change / Switch Medall Branch in Step 2 */}
                    {isMedallLab(selectedLab) && medallBranches.length > 0 && (
                      <div className="mb-4 p-3 rounded-3 border bg-white shadow-sm">
                        <label htmlFor="medallBranchSelect" className="form-label small fw-semibold text-primary mb-2 d-flex align-items-center gap-1">
                          <i className="material-icons" style={{ fontSize: "1rem" }}>storefront</i>
                          Select / Change Medall Branch <span className="text-danger">*</span>
                        </label>
                        <select
                          id="medallBranchSelect"
                          className="form-select"
                          value={selectedBranch ? selectedBranch.BranchId : ""}
                          onChange={(e) => {
                            const found = medallBranches.find(b => String(b.BranchId) === String(e.target.value));
                            setSelectedBranch(found || null);
                          }}
                        >
                          {medallBranches.map((b) => (
                            <option key={b.BranchId} value={b.BranchId}>
                              {b.BranchName} ({b.BranchCode}) - {b.CityName || ""}{b.Pincode ? ` - PIN ${b.Pincode}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {companyRes?.packageByCategory === true ? (
                      <div>
                        {/* ── Category Tab Bar (Pilots, Cabin Crew, FATA Pilots, ATCOs) ── */}
                        <div className="mb-4">
                          <p className="bcat-section-label">1. Select Candidate Category</p>
                          <div className="bcat-tab-bar">
                            {bookingCategories.map((cat) => {
                              const isSelected = String(cat.id) === String(selectedCategoryId);
                              const catName = cat.name || cat.categoryname;
                              const icon = catName?.toLowerCase().includes("pilot") ? "flight"
                                : catName?.toLowerCase().includes("crew") ? "badge"
                                  : catName?.toLowerCase().includes("atco") ? "radar"
                                    : "category";
                              return (
                                <button
                                  key={cat.id}
                                  type="button"
                                  className={`bcat-tab-btn${isSelected ? " active" : ""}`}
                                  onClick={() => {
                                    setSelectedCategoryId(cat.id);
                                    handlePackageSelection({ target: { value: "" } });
                                  }}
                                >
                                  <i className="material-icons bcat-tab-icon">{icon}</i>
                                  {catName}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* ── Package Type Toggle (Initial / Renewal) ── */}
                        {selectedCategoryId && (
                          <div className="mb-4">
                            <p className="bcat-section-label">2. Select Examination Type</p>
                            <div className="bcat-type-toggle">
                              {["Initial", "Renewal"].map((type) => (
                                <button
                                  key={type}
                                  type="button"
                                  className={`bcat-type-btn${selectedPackageType === type ? " active" : ""}`}
                                  onClick={() => {
                                    setSelectedPackageType(type);
                                    handlePackageSelection({ target: { value: "" } });
                                  }}
                                >
                                  {type}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* ── Package Cards / Selected Package Details ── */}
                        {!packageId ? (
                          <div className="mb-4">
                            <p className="bcat-section-label">
                              3. Select Medical Package &mdash; {selectedPackageType}
                            </p>
                            {(() => {
                              const categoryPackages = packages.filter(
                                (pkg) =>
                                  (Array.isArray(pkg.bookingCategoryIds)
                                    ? pkg.bookingCategoryIds.map(String).includes(String(selectedCategoryId))
                                    : String(pkg.bookingCategoryId) === String(selectedCategoryId)) &&
                                  (!selectedPackageType || pkg.packageType === selectedPackageType)
                              );

                              if (categoryPackages.length === 0) {
                                return (
                                  <div className="bcat-empty">
                                    <i className="material-icons">event_busy</i>
                                    <p>No packages found for <strong>{selectedPackageType}</strong> under this category.</p>
                                  </div>
                                );
                              }

                              return (
                                <div className="bcat-pkg-grid">
                                  {categoryPackages.map((pkg) => (
                                    <div
                                      key={pkg.id}
                                      className="bcat-pkg-card"
                                      onClick={() => handlePackageSelection({ target: { value: pkg.id } })}
                                    >
                                      <div>
                                        <p className="bcat-pkg-name">{pkg.packagename}</p>
                                        {pkg.description && (
                                          <p className="bcat-pkg-desc">{pkg.description}</p>
                                        )}
                                        {pkg.price > 0 && (
                                          <span className="bcat-pkg-price">₹{pkg.price.toLocaleString()}</span>
                                        )}
                                      </div>
                                      <i className="material-icons bcat-pkg-arrow">chevron_right</i>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        ) : (
                          <div className="bcat-selected-box mb-4">
                            <div className="bcat-selected-header">
                              <div>
                                <span className="bcat-selected-type-badge">
                                  <i className="material-icons" style={{ fontSize: "0.85rem" }}>verified</i>
                                  {selectedPackageType} Medical
                                </span>
                                <h5 className="bcat-selected-name">{tests}</h5>
                                {packagePrice > 0 && (
                                  <p className="bcat-selected-price mb-0">Total Price: ₹{packagePrice.toLocaleString()}</p>
                                )}
                              </div>
                              <button
                                type="button"
                                className="bcat-change-btn"
                                onClick={() => handlePackageSelection({ target: { value: "" } })}
                              >
                                <i className="material-icons" style={{ fontSize: "1rem" }}>swap_horiz</i>
                                Change
                              </button>
                            </div>

                            {/* Investigations Included */}
                            <div className="bcat-investigations">
                              <div className="bcat-investigations-header">
                                <span className="bcat-investigations-title">
                                  <i className="material-icons">checklist</i>
                                  Investigations Included
                                </span>
                                <span className="bcat-investigations-count">{packageTests.length} tests</span>
                              </div>
                              {packageTests.length === 0 ? (
                                <div className="bcat-empty" style={{ padding: "1.5rem" }}>
                                  <i className="material-icons" style={{ fontSize: "1.5rem" }}>science_off</i>
                                  <p>No tests mapped to this package.</p>
                                </div>
                              ) : (
                                <div className="bcat-investigations-list">
                                  {packageTests.map((t, idx) => (
                                    <div key={t.id || idx} className="bcat-investigation-item">
                                      <i className="material-icons">check_circle</i>
                                      <span className="bcat-investigation-name">
                                        {t.lab?.testname || t.labTest?.testname || t.testname || t.testName || "Lab Test"}
                                        {t.lab?.testCat?.categoryname ? ` (${t.lab.testCat.categoryname})` : ""}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : designationMappings.length > 0 ? (
                      <div className="mb-4">
                        <label htmlFor="designationPackage" className="form-label small fw-semibold">
                          Select Package <span className="text-danger">*</span>
                        </label>
                        <select
                          id="designationPackage"
                          className="form-select"
                          value={packageId}
                          onChange={handlePackageSelection}
                        >
                          <option value="">-- Select Package --</option>
                          {designationMappings.map((mapping) =>
                            mapping.pac ? (
                              <option key={mapping.id} value={mapping.packageid}>
                                {mapping.pac.packagename}
                                {companyRes?.pilotOrganization === true && mapping.pac.price > 0
                                  ? ` - ₹${mapping.pac.price}`
                                  : ""}
                              </option>
                            ) : null
                          )}
                        </select>
                      </div>
                    ) : packages && packages.length > 0 ? (
                      <div className="mb-4">
                        <label htmlFor="package" className="form-label small fw-semibold">
                          Select Package <span className="text-danger">*</span>
                        </label>
                        <select id="package" className="form-select" value={packageId} onChange={handlePackageSelection}>
                          <option value="">-- Select Package --</option>
                          {packages.map((pkg) => (
                            <option key={pkg.id} value={pkg.id}>
                              {pkg.packagename} {companyRes?.pilotOrganization === true && pkg.price > 0 ? `- ₹${pkg.price}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                  </div>

                  {/* Right Column: Schedule Date, Additional Tests, Home Collection, BMI */}
                  <div className="col-lg-6 ps-lg-4">
                    {/* Date Picker Form Card */}
                    <div className="bcat-form-card">
                      <div className="bcat-form-card-title">
                        <i className="material-icons">event</i>
                        Appointment Date
                      </div>
                      <div>
                        <label htmlFor="date" className="form-label small text-muted">
                          Choose Preferred Date <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          id="date"
                          className="bcat-input"
                          value={date}
                          onChange={(e) => setDate(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Additional Tests Form Card */}
                    <div
                      className="bcat-form-card position-relative"
                      style={{
                        // Allow the absolute test dropdown to escape the card (base class clips via overflow:hidden)
                        overflow: "visible",
                        // Raise this card above later siblings while the dropdown is open
                        zIndex: showTestDropdown ? 30 : "auto",
                      }}
                    >
                      <div className="bcat-form-card-title">
                        <i className="material-icons">science</i>
                        Additional Investigations
                      </div>
                      <div className="position-relative" ref={testPickerRef}>
                        <button
                          type="button"
                          className={`bcat-test-picker${showTestDropdown ? " is-open" : ""}`}
                          onClick={() => {
                            const next = !showTestDropdown;
                            setShowTestDropdown(next);
                            if (!next) setTestSearchQuery("");
                          }}
                        >
                          <i className="material-icons bcat-test-picker__icon">search</i>
                          <span
                            className={`bcat-test-picker__text${
                              selectedTests.filter((t) => t.testSource === "Manual").length === 0
                                ? " is-placeholder"
                                : ""
                            }`}
                          >
                            {selectedTests.filter((t) => t.testSource === "Manual").length > 0
                              ? `${selectedTests.filter((t) => t.testSource === "Manual").length} optional test${
                                  selectedTests.filter((t) => t.testSource === "Manual").length === 1 ? "" : "s"
                                } selected`
                              : "Search & select optional tests"}
                          </span>
                          {selectedTests.filter((t) => t.testSource === "Manual").length > 0 && (
                            <span className="bcat-test-picker__count">
                              {selectedTests.filter((t) => t.testSource === "Manual").length}
                            </span>
                          )}
                          <i
                            className={`material-icons bcat-test-picker__chevron${
                              showTestDropdown ? " is-up" : ""
                            }`}
                          >
                            expand_more
                          </i>
                        </button>

                        {showTestDropdown && (
                          <div className="bcat-test-dropdown">
                            {/* Search header */}
                            <div className="bcat-test-dropdown__search">
                              <i className="material-icons">search</i>
                              <input
                                type="text"
                                autoFocus
                                placeholder="Search tests..."
                                value={testSearchQuery}
                                onChange={(e) => setTestSearchQuery(e.target.value)}
                              />
                              {testSearchQuery && (
                                <button
                                  type="button"
                                  className="bcat-test-dropdown__clear"
                                  onClick={() => setTestSearchQuery("")}
                                  aria-label="Clear search"
                                >
                                  <i className="material-icons">close</i>
                                </button>
                              )}
                            </div>

                            {/* Results list */}
                            <div className="bcat-test-dropdown__list">
                              {(() => {
                                const query = testSearchQuery.trim().toLowerCase();
                                const availableTests = labTests
                                  .filter((test) => {
                                    if (!packageId || packageTests.length === 0) return true;
                                    return !packageTests.some((pt) => {
                                      const pkgTestId = pt.lab?.id || pt.labTest?.id || pt.testid || pt.id;
                                      const pkgTestName = (pt.lab?.testname || pt.labTest?.testname || pt.testname || "").toLowerCase().trim();
                                      const currentTestName = (test.testname || "").toLowerCase().trim();
                                      return (
                                        (pkgTestId && Number(pkgTestId) === Number(test.id)) ||
                                        (pkgTestName && currentTestName && pkgTestName === currentTestName)
                                      );
                                    });
                                  })
                                  .filter(
                                    (test) =>
                                      !query ||
                                      (test.testname || "").toLowerCase().includes(query) ||
                                      (test.testCat?.categoryname || "").toLowerCase().includes(query)
                                  );

                                if (availableTests.length === 0) {
                                  return (
                                    <div className="bcat-test-dropdown__empty">
                                      <i className="material-icons">{query ? "search_off" : "checklist"}</i>
                                      <span>
                                        {query
                                          ? `No tests match "${testSearchQuery.trim()}"`
                                          : "All available tests are already included in the selected package."}
                                      </span>
                                    </div>
                                  );
                                }

                                return availableTests.map((test) => {
                                  const isChecked = selectedTests.some((t) => t.testId === test.id);
                                  const isRecommended = apiRecommendedTests.some(
                                    (rt) => Number(rt.testId) === Number(test.id)
                                  );
                                  return (
                                    <label
                                      key={test.id}
                                      className={`bcat-test-option${isChecked ? " is-checked" : ""}`}
                                      htmlFor={`test-${test.id}`}
                                    >
                                      <input
                                        className="form-check-input m-0 bcat-test-option__check"
                                        type="checkbox"
                                        id={`test-${test.id}`}
                                        checked={isChecked}
                                        onChange={(e) => handleTestCheckboxChange(test.id, e.target.checked)}
                                      />
                                      <span className="bcat-test-option__body">
                                        <span className="bcat-test-option__name">
                                          {test.testname || "Unnamed test"}
                                        </span>
                                        <span className="bcat-test-option__meta">
                                          {test.testCat?.categoryname && <span>{test.testCat.categoryname}</span>}
                                          {test.testCost?.cost ? (
                                            <span className="bcat-test-option__price">₹{test.testCost.cost}</span>
                                          ) : null}
                                        </span>
                                      </span>
                                      {isRecommended && (
                                        <span className="bcat-test-option__badge">Recommended</span>
                                      )}
                                    </label>
                                  );
                                });
                              })()}
                            </div>

                            {/* Footer */}
                            <div className="bcat-test-dropdown__footer">
                              <span>
                                <strong>{selectedTests.length}</strong> selected · {labTests.length} available
                              </span>
                              {selectedTests.some((t) => t.testSource === "Manual") && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setSelectedTests((prev) => prev.filter((t) => t.testSource !== "Manual"))
                                  }
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {selectedTests.length > 0 && (
                        <div className="mt-3">
                          <h6 className="bcat-section-label mb-2">Selected Additional Tests</h6>
                          <div className="d-flex flex-wrap gap-2">
                            {selectedTests.map((item) => {
                              const id = item.testId;
                              const selectedTest = labTests.find((t) => t.id === Number(id));
                              return (
                                <span
                                  key={id}
                                  className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-3 py-2 rounded-pill small cursor-pointer d-flex align-items-center gap-1"
                                  onClick={() => handleRemoveTest(id)}
                                >
                                  {selectedTest?.testname || "Test"} ✕
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Home Collection — only shown when company has homeCollection enabled */}
                    {companyRes?.homeCollection === true && (packageId || selectedTests.length > 0) && homeCollectionAvailable !== null && (
                      <div className="bcat-form-card">
                        <div className="bcat-form-card-title">
                          <i className="material-icons">home_work</i>
                          Home Collection Service
                        </div>
                        {homeCollectionAvailable && !selectedTests.some((st) => labTests.find((lt) => lt.id === Number(st.testId))?.labMandatory === true) ? (
                          <div
                            className="d-flex align-items-center justify-content-between p-3 rounded-3"
                            style={{ background: "rgba(34, 197, 94, 0.08)", border: "1px solid rgba(34, 197, 94, 0.3)" }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <i className="material-icons text-success" style={{ fontSize: "1.3rem" }}>home</i>
                              <div>
                                <span className="small fw-bold text-success d-block">Home Collection Available</span>
                                <span className="small text-muted" style={{ fontSize: "0.75rem" }}>Sample collection at your location</span>
                              </div>
                            </div>
                            <div className="form-check form-switch m-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="homeCollectionCheckbox"
                                checked={homeCollection}
                                onChange={(e) => setHomeCollection(e.target.checked)}
                                style={{ cursor: "pointer", width: "2.5em", height: "1.3em" }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="d-flex align-items-center gap-2 p-3 rounded-3" style={{ background: "rgba(239, 68, 68, 0.08)", border: "1px solid rgba(239, 68, 68, 0.3)" }}>
                            <i className="material-icons text-danger" style={{ fontSize: "1.3rem" }}>domain_disabled</i>
                            <div>
                              <span className="small text-danger fw-bold d-block">Center Visit Required</span>
                              <span className="small text-muted" style={{ fontSize: "0.75rem" }}>Home collection is not available for selected items</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* BMI Calculator Form Card */}
                    {companyRes?.bmiCalculator && (
                      <div className="bcat-form-card">
                        <div className="bcat-form-card-title">
                          <i className="material-icons">monitor_weight</i>
                          BMI Calculator
                        </div>
                        <div className="row g-3">
                          <div className="col-6">
                            <label htmlFor="weight" className="form-label small text-muted">Weight (kg)</label>
                            <input
                              type="number"
                              id="weight"
                              className="bcat-input"
                              placeholder="e.g. 70"
                              value={weight}
                              onChange={(e) => setWeight(e.target.value)}
                            />
                          </div>
                          <div className="col-6">
                            <label htmlFor="height" className="form-label small text-muted">Height (cm)</label>
                            <input
                              type="number"
                              id="height"
                              className="bcat-input"
                              placeholder="e.g. 175"
                              value={height}
                              onChange={(e) => setHeight(e.target.value)}
                            />
                          </div>
                          <div className="col-12">
                            {bmiScore ? (
                              <>
                                <div className="alert alert-success d-flex justify-content-between align-items-center m-0 py-2 border-0 rounded-3">
                                  <span className="fw-bold small">BMI Score: {bmiScore}</span>
                                  {apiRecommendedTests.some((rt) => rt.ruleName === "BMI") && (
                                    <span className="badge bg-danger small">RULE MATCHED</span>
                                  )}
                                </div>

                                {/* Recommended Tests Below BMI */}
                                <div className="mt-3 pt-3 border-top">
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <span className="bcat-section-label m-0" style={{ fontSize: "0.72rem" }}>
                                      <i className="material-icons text-warning me-1" style={{ fontSize: "0.95rem", verticalAlign: "middle" }}>stars</i>
                                      Recommended Tests
                                    </span>
                                    {apiRecommendedTests.length > 0 && (
                                      <span className="badge bg-warning text-dark small" style={{ fontSize: "0.68rem" }}>
                                        {apiRecommendedTests.length} Recommended
                                      </span>
                                    )}
                                  </div>

                                  {apiRecommendedTests.length === 0 ? (
                                    <p className="text-muted small m-0 fst-italic">No additional tests recommended for this BMI range.</p>
                                  ) : (
                                    <div className="d-flex flex-column gap-2">
                                      {apiRecommendedTests.map((rt) => {
                                        const testObj = labTests.find((lt) => Number(lt.id) === Number(rt.testId));
                                        const isSelected = selectedTests.some((st) => Number(st.testId) === Number(rt.testId));
                                        const isAlreadyInPkg = packageTests.some((pt) => {
                                          const pkgTestId = pt.lab?.id || pt.labTest?.id || pt.testid || pt.id;
                                          const pkgTestName = (pt.lab?.testname || pt.labTest?.testname || pt.testname || "").toLowerCase().trim();
                                          const rtName = (testObj?.testname || "").toLowerCase().trim();
                                          return (
                                            (pkgTestId && Number(pkgTestId) === Number(rt.testId)) ||
                                            (pkgTestName && rtName && pkgTestName === rtName)
                                          );
                                        });

                                        return (
                                          <div
                                            key={rt.testId}
                                            className={`d-flex align-items-center justify-content-between p-2 rounded-3 border bcat-rec-test-row${
                                              isSelected
                                                ? " bcat-rec-test-row--selected"
                                                : isAlreadyInPkg
                                                  ? " bcat-rec-test-row--in-package"
                                                  : ""
                                            }`}
                                          >
                                            <div className="d-flex align-items-center gap-2">
                                              <input
                                                type="checkbox"
                                                className="form-check-input m-0"
                                                id={`bmi-rec-test-${rt.testId}`}
                                                checked={isSelected || isAlreadyInPkg}
                                                disabled={isAlreadyInPkg}
                                                onChange={(e) => handleTestCheckboxChange(rt.testId, e.target.checked)}
                                                style={{ cursor: isAlreadyInPkg ? "not-allowed" : "pointer" }}
                                              />
                                              <label
                                                htmlFor={`bmi-rec-test-${rt.testId}`}
                                                className="small fw-semibold m-0"
                                                style={{ cursor: isAlreadyInPkg ? "not-allowed" : "pointer" }}
                                              >
                                                {testObj?.testname || `Test #${rt.testId}`}
                                                {testObj?.testCat?.categoryname ? ` (${testObj.testCat.categoryname})` : ""}
                                                {testObj?.testCost?.cost ? ` - ₹${testObj.testCost.cost}` : ""}
                                              </label>
                                            </div>

                                            {isAlreadyInPkg ? (
                                              <span className="badge bg-secondary small" style={{ fontSize: "0.68rem" }}>
                                                In Package
                                              </span>
                                            ) : (
                                              <span className="badge bg-warning text-dark small" style={{ fontSize: "0.68rem" }}>
                                                RECOMMENDED
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </>
                            ) : (
                              <div className="text-muted small text-center py-2 bg-light rounded border">
                                Enter weight and height to calculate BMI
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Medall Service List (shown only after package is selected) ── */}
              {isMedallLab(selectedLab) && selectedBranch && packageId && (serviceLoader || medallServiceList.length > 0) && (
                <div className="mt-4 p-3 rounded-3 border bg-white shadow-sm">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <i className="material-icons text-primary" style={{ fontSize: "1.2rem" }}>biotech</i>
                      <span className="fw-bold text-dark small">Medall Service List</span>
                      {medallServiceList.length > 0 && (
                        <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill small">
                          {selectedMedallServices.length}/{medallServiceList.length} selected
                        </span>
                      )}
                    </div>
                    <span className="small text-muted" style={{ fontSize: "0.75rem" }}>
                      Branch: <strong>{selectedBranch.BranchName}</strong>
                    </span>
                  </div>

                  {serviceLoader ? (
                    <div className="d-flex align-items-center justify-content-center py-3 gap-2 text-primary">
                      <div className="spinner-border spinner-border-sm" role="status"></div>
                      <span className="small fw-semibold">Loading service list from Medall...</span>
                    </div>
                  ) : medallServiceList.length === 0 ? (
                    <div className="text-center py-3 text-muted small">
                      <i className="material-icons d-block mb-1" style={{ fontSize: "1.8rem" }}>science_off</i>
                      No matching services found for this package at the selected branch.
                    </div>
                  ) : (
                    <div className="row g-2" style={{ maxHeight: "280px", overflowY: "auto" }}>
                      <div className="col-12">
                        <div
                          className="d-flex align-items-center gap-2 p-2 rounded-3 border bg-white"
                          style={{ position: "sticky", top: 0, zIndex: 1 }}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input m-0"
                            id="medall-select-all-services"
                            checked={allMedallServicesSelected}
                            onChange={(e) => handleToggleAllMedallServices(e.target.checked)}
                            style={{ cursor: "pointer" }}
                          />
                          <label
                            htmlFor="medall-select-all-services"
                            className="small fw-semibold m-0 text-muted"
                            style={{ cursor: "pointer" }}
                          >
                            Select all services
                          </label>
                        </div>
                      </div>
                      {medallServiceList.map((svc, idx) => {
                        const serviceKey = String(getServiceKey(svc, idx));
                        const isChecked = !unselectedMedallServiceKeys.includes(serviceKey);
                        const matchType = svc._matchType || "related";
                        const matchBadge = matchType === "exact"
                          ? { label: "Exact Match",        bg: "rgba(34,197,94,0.12)",  color: "#15803d",  border: "rgba(34,197,94,0.4)"  }
                          : matchType === "abbreviation"
                          ? { label: "Abbreviation Match", bg: "rgba(99,102,241,0.10)", color: "#4f46e5",  border: "rgba(99,102,241,0.35)" }
                          : { label: "Related",            bg: "rgba(148,163,184,0.10)",color: "#64748b",  border: "rgba(148,163,184,0.35)" };
                        return (
                          <div key={serviceKey} className="col-md-6">
                            <div
                              className="p-2 rounded-3 border d-flex align-items-start gap-2"
                              style={{
                                fontSize: "0.82rem",
                                background: isChecked
                                  ? "rgba(14, 165, 233, 0.12)"
                                  : "rgba(255, 255, 255, 0.04)",
                                borderColor: isChecked
                                  ? "#0ea5e9"
                                  : "rgba(255, 255, 255, 0.12)",
                                opacity: isChecked ? 1 : 0.65,
                                cursor: "pointer",
                                transition: "all 0.15s ease"
                              }}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input m-0 mt-1"
                                id={`medall-svc-${serviceKey}`}
                                checked={isChecked}
                                onChange={(e) => handleMedallServiceToggle(serviceKey, e.target.checked, svc)}
                                style={{ cursor: "pointer" }}
                              />
                              <label
                                htmlFor={`medall-svc-${serviceKey}`}
                                className="m-0 flex-grow-1"
                                style={{ cursor: "pointer" }}
                              >
                                <div className="fw-semibold mb-1" style={{ color: "inherit" }}>{svc.ServiceName || svc.serviceName || svc.Name || "Service"}</div>
                                <div className="d-flex flex-wrap gap-1 align-items-center">
                                  {/* Match-type badge */}
                                  <span
                                    style={{
                                      fontSize: "0.65rem",
                                      fontWeight: 600,
                                      padding: "1px 7px",
                                      borderRadius: "20px",
                                      background: matchBadge.bg,
                                      color: matchBadge.color,
                                      border: `1px solid ${matchBadge.border}`,
                                      letterSpacing: "0.02em",
                                      whiteSpace: "nowrap"
                                    }}
                                  >
                                    {matchType === "exact" ? "✓ " : matchType === "abbreviation" ? "≈ " : "~ "}
                                    {matchBadge.label}
                                  </span>
                                  {svc.ServiceCode && (
                                    <span className="badge bg-secondary bg-opacity-10 text-secondary" style={{ fontSize: "0.65rem" }}>
                                      Code: {svc.ServiceCode}
                                    </span>
                                  )}
                                  {svc.Rate !== undefined && svc.Rate !== null && (
                                    <span className="badge bg-success bg-opacity-10 text-success" style={{ fontSize: "0.65rem" }}>
                                      ₹{svc.Rate}
                                    </span>
                                  )}
                                </div>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Total Price & Action Navigation Bar */}
              {(() => {
                const totalAddonsPrice = selectedTests.reduce((acc, st) => {
                  const found = labTests.find((lt) => Number(lt.id) === Number(st.testId));
                  return acc + (found?.testCost?.cost ? Number(found.testCost.cost) : 0);
                }, 0);
                const calculatedTotalPrice = (Number(packagePrice) || 0) + totalAddonsPrice;

                return (
                  <div className="bcat-total-bar">
                    <div>
                      <div className="bcat-total-label">Total Price</div>
                      <div className="bcat-total-amount-row">
                        <span className="bcat-total-price">
                          ₹ {calculatedTotalPrice > 0 ? calculatedTotalPrice.toLocaleString() : "0"}/-
                        </span>
                        <span className="bcat-total-subtext">
                          (Inclusive of standard investigations)
                        </span>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <Button
                        variant="outline-secondary"
                        className="rounded-pill px-3 py-2 btn-sm"
                        onClick={() => setStep(1)}
                      >
                        ◄ Back
                      </Button>
                      <button
                        className="btn-hm-primary px-4 py-2 fw-bold shadow rounded-pill d-flex align-items-center gap-2"
                        onClick={handleBookAppointment}
                      >
                        <span>Book Appointment</span>
                        <i className="material-icons" style={{ fontSize: "1.1rem" }}>arrow_forward</i>
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <AdminDashboard />
      {loader && <div className="loading"></div>}

      <div className="col-12 mx-xl-auto ps-xl-10 mt-4 pe-xl-4">
        <div className="hm-page-wrapper">
          {/* Header */}
          <div className="hm-page-header mb-3">
            <div>
              <h2 className="hm-page-title">
                <i className="material-icons">event</i>
                Book Lab Appointment
              </h2>
              <p className="hm-page-subtitle">
                Select a diagnostic lab center and configure your medical appointment details.
              </p>
            </div>
          </div>

          {/* Stepper Header */}
          <div className="d-flex align-items-center justify-content-center gap-3 mb-3 pb-2 border-bottom">
            <div
              className={`d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold ${step === 1
                  ? "bg-primary text-white shadow-sm"
                  : "bg-light text-secondary border"
                }`}
            >
              <span
                className={`rounded-circle d-flex align-items-center justify-content-center fw-bold ${step === 1 ? "bg-white text-primary" : "bg-secondary text-white"
                  }`}
                style={{ width: "24px", height: "24px", fontSize: "12px" }}
              >
                1
              </span>
              <span>Select Lab</span>
            </div>

            <div className="text-muted">➔</div>

            <div
              className={`d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold ${step === 2
                  ? "bg-primary text-white shadow-sm"
                  : "bg-light text-secondary border"
                }`}
            >
              <span
                className={`rounded-circle d-flex align-items-center justify-content-center fw-bold ${step === 2 ? "bg-white text-primary" : "bg-secondary text-white"
                  }`}
                style={{ width: "24px", height: "24px", fontSize: "12px" }}
              >
                2
              </span>
              <span>Book Appointment</span>
            </div>
          </div>

          {renderStepContent(step)}
        </div>
      </div>

      {isTestsModalOpen && (
        <TestForPackage
          isOpen={isTestsModalOpen}
          onRequestClose={closeTestsModal}
          packageId={selectedPackage}
        />
      )}

      {/* ── Exact-match Service Selection Modal ── */}
      <Modal
        show={exactMatchModalOpen}
        onHide={() => setExactMatchModalOpen(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-bottom px-4 py-3">
          <Modal.Title className="h6 fw-bold mb-0 text-primary d-flex align-items-center gap-2">
            <i className="material-icons" style={{ fontSize: "1.1rem" }}>biotech</i>
            Multiple Exact Service Matches Found
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="small text-muted mb-3">
            The following test names matched multiple services in Medall with the exact same name.
            Please select the correct service for each test:
          </p>
          {exactMatchGroups.map((group) => (
            <div key={group.testName} className="mb-4 p-3 rounded-3 border bg-light">
              <p className="fw-semibold small mb-2 text-dark d-flex align-items-center gap-1">
                <i className="material-icons text-primary" style={{ fontSize: "1rem" }}>science</i>
                Test: <strong className="ms-1">{group.testName}</strong>
              </p>
              <div className="d-flex flex-column gap-2">
                {group.matches.map((svc) => {
                  const svcKey = String(svc.ServiceId ?? svc.ServiceCode ?? svc.ServiceName ?? svc.serviceName ?? "");
                  const isSelected = exactMatchSelections[group.testName] &&
                    String(exactMatchSelections[group.testName].ServiceId ?? exactMatchSelections[group.testName].ServiceCode ?? exactMatchSelections[group.testName].ServiceName ?? "") === svcKey;
                  return (
                    <div
                      key={svcKey}
                      className="d-flex align-items-center gap-2 p-2 rounded-3 border bg-white"
                      style={{
                        cursor: "pointer",
                        borderColor: isSelected ? "#2563eb" : "#e2e8f0",
                        background: isSelected ? "rgba(37,99,235,0.07)" : "#fff"
                      }}
                      onClick={() =>
                        setExactMatchSelections((prev) => ({ ...prev, [group.testName]: svc }))
                      }
                    >
                      <input
                        type="radio"
                        className="form-check-input m-0"
                        readOnly
                        checked={!!isSelected}
                        style={{ cursor: "pointer" }}
                      />
                      <div className="flex-grow-1">
                        <div className="fw-semibold small text-dark">
                          {svc.ServiceName || svc.serviceName || svc.Name || "Service"}
                        </div>
                        <div className="d-flex gap-2 flex-wrap mt-1">
                          {svc.ServiceCode && (
                            <span className="badge bg-secondary bg-opacity-10 text-secondary" style={{ fontSize: "0.68rem" }}>
                              Code: {svc.ServiceCode}
                            </span>
                          )}
                          {svc.Rate !== undefined && svc.Rate !== null && (
                            <span className="badge bg-success bg-opacity-10 text-success" style={{ fontSize: "0.68rem" }}>
                              ₹{svc.Rate}
                            </span>
                          )}
                          {svc.ServiceId && (
                            <span className="badge bg-primary bg-opacity-10 text-primary" style={{ fontSize: "0.68rem" }}>
                              ID: {svc.ServiceId}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer className="border-top px-4 py-2">
          <Button
            variant="outline-secondary"
            className="rounded-pill btn-sm px-3"
            onClick={() => setExactMatchModalOpen(false)}
          >
            Cancel
          </Button>
          <button
            className="btn-hm-primary px-3 py-1"
            disabled={exactMatchGroups.some((g) => !exactMatchSelections[g.testName])}
            onClick={() => {
              // For each group, unselect all sibling matches except the user's pick
              const toUnselect = [];
              exactMatchGroups.forEach((group) => {
                const picked = exactMatchSelections[group.testName];
                if (!picked) return;
                group.matches.forEach((svc) => {
                  const svcKey = String(getServiceKey(svc, -1));
                  const pickedKey = String(getServiceKey(picked, -1));
                  if (svcKey !== pickedKey) toUnselect.push(svcKey);
                });
              });
              setUnselectedMedallServiceKeys((prev) => [
                ...prev.filter((k) => !toUnselect.includes(k)),
                ...toUnselect.filter((k) => !prev.includes(k))
              ]);
              setExactMatchModalOpen(false);
            }}
          >
            Confirm Selections
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)} centered>
        <Modal.Header closeButton className="border-bottom px-4 py-3">
          <Modal.Title className="h6 fw-bold mb-0 text-primary">Appointment Authorization</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="small text-muted mb-0">
            On confirmation of appointment with the selected lab{selectedBranch ? ` (${selectedBranch.BranchName})` : ""}, I hereby authorize the lab to upload the medical reports to the TMPL web portal for <strong>{companyRes?.companyname}</strong>.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-top px-4 py-2">
          <Button variant="outline-secondary" className="rounded-pill btn-sm px-3" onClick={() => setShowConfirmationModal(false)}>
            Cancel
          </Button>
          <button className="btn-hm-primary px-3 py-1" onClick={confirmBookAppointment}>
            Confirm & Book
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default BookAnAppointment;
